/**
 * Partners management page.
 * List with {@link SlidePanel} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { SlidePanel } from '@/components/common/SlidePanel';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePartnerPageActions } from '@/components/partners/partner-table-utils';
import { useDetailsView } from '@/hooks/useDetailsView';
import { CreatePartnerView } from '@/components/partners/CreatePartnerView';
import { EditPartnerView } from '@/components/partners/EditPartnerView';
import { PartnersDataTable } from '@/components/partners/PartnersDataTable';
import { partnersKeys } from '@/hooks/usePartners';
import type { Partner } from '@/types/partner.types';

/** Partners list with {@link SlidePanel} create/edit. */
export const PartnersManagementPage: React.FC = () => {
  const partnerActions = usePartnerPageActions();
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editingPartnerId, setEditingPartnerId] = React.useState<string | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  /** Deep-link: open create/edit/view via URL params, then strip them. */
  React.useEffect(() => {
    const shouldCreate = searchParams.get('create') === 'true';
    const editId = searchParams.get('edit');
    const viewId = searchParams.get('view');

    if (editId) {
      setEditingPartnerId(editId);
      closeRow();
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    } else if (viewId) {
      setOpenRow(viewId);
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    } else if (shouldCreate) {
      setCreateOpen(true);
      closeRow();
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, closeRow, openRow]);

  const anyFormOpen = createOpen || editingPartnerId !== null;
  const prevAnyFormOpenRef = React.useRef(anyFormOpen);
  React.useEffect(() => {
    if (!prevAnyFormOpenRef.current && anyFormOpen && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevAnyFormOpenRef.current = anyFormOpen;
  }, [anyFormOpen]);

  const handleView = (partner: Partner) => {
    openRow(partner.id);
  };

  const handleEdit = (partner: Partner) => {
    setEditingPartnerId(partner.id);
    closeRow();
  };

  const handleOpenCreate = () => {
    setCreateOpen(true);
    closeRow();
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
  };

  const handleCloseEdit = () => {
    setEditingPartnerId(null);
  };

  const handleSuccess = () => {
    setCreateOpen(false);
    setEditingPartnerId(null);
    closeRow();
    void queryClient.invalidateQueries({ queryKey: partnersKeys.all });
  };

  return (
    <ManagementPage
      title="Gestion des Partenaires"
      subtitle="Créez, modifiez et gérez tous vos partenaires"
      contentRef={contentRef}
      actions={
        partnerActions.canCreatePartner ? (
          <Button preset="create-entity" onClick={handleOpenCreate}>
            Créer un partenaire
          </Button>
        ) : undefined
      }
    >
      <PartnersDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        pageActions={{
          canView: partnerActions.canViewPartner,
          canEdit: partnerActions.canEditPartner,
          canDelete: partnerActions.canDeletePartner,
          canExportExcel: partnerActions.canExportPartners,
        }}
        onView={handleView}
        onEdit={handleEdit}
      />

      <SlidePanel isOpen={createOpen} onClose={handleCloseCreate}>
        <CreatePartnerView onClose={handleCloseCreate} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editingPartnerId !== null} onClose={handleCloseEdit}>
        {editingPartnerId ? (
          <EditPartnerView
            key={editingPartnerId}
            partnerId={editingPartnerId}
            onClose={handleCloseEdit}
            onSuccess={handleSuccess}
          />
        ) : null}
      </SlidePanel>
    </ManagementPage>
  );
};
